// src/lib/fishiq/fishIQWaterProfile.ts
// FishIQ Water Intelligence foundation.
// Pure helper: no map side effects, no Firestore, no network calls.

import {
  buildFishIQLocationContext,
  type FishIQLocationContext,
} from '../../services/fishIQLocationRanker';
import { resolveWaterContext } from '../../services/fishIQWaterContext';
import type { FishIQWaterProfile, FishIQWaterType } from '../../types';

export type FishIQWaterProfileInput = {
  lat?: number | null;
  lng?: number | null;
  lon?: number | null;
  waterName?: string | null;
  waterType?: string | null;
  waterKind?: string | null;
  regionHint?: string | null;
  source?: string | null;
  season?: string | null;
  timeOfDay?: string | null;
};

const clean = (value: any) => String(value ?? '').trim();
const lower = (value: any) => clean(value).toLowerCase();
const finiteNumberOrNull = (value: any): number | null => {
  const next = Number(value);
  return Number.isFinite(next) ? next : null;
};

const currentSeason = (): FishIQWaterProfile['season'] => {
  const month = new Date().getMonth() + 1;
  if (month >= 3 && month <= 5) return 'spring';
  if (month >= 6 && month <= 8) return 'summer';
  if (month >= 9 && month <= 11) return 'fall';
  return 'winter';
};

const currentTimeBucket = (): FishIQWaterProfile['timeOfDay'] => {
  const hour = new Date().getHours();
  if (hour >= 4 && hour < 9) return 'dawn';
  if (hour >= 9 && hour < 17) return 'day';
  if (hour >= 17 && hour < 21) return 'dusk';
  return 'night';
};

export function normalizeFishIQWaterType(input: {
  waterType?: string | null;
  waterKind?: string | null;
  waterName?: string | null;
  contextClass?: string | null;
}): FishIQWaterType {
  // The shared resolver is authoritative. Honor an explicit resolved inland
  // context before scanning free-text names so names such as "Laguna de Bay"
  // cannot be reclassified as saltwater merely because they contain "bay".
  if (input.contextClass === 'freshwater' || input.contextClass === 'inland') {
    return 'freshwater';
  }
  if (input.contextClass === 'offshore') return 'saltwater';

  const hay = [input.waterType, input.waterKind, input.waterName]
    .map(lower)
    .filter(Boolean)
    .join(' ');

  if (/\b(brackish|estuary|estuarine|lagoon|delta|tidal|river mouth|bay mouth|marsh)\b/.test(hay)) return 'brackish';
  if (/\b(saltwater|salt water|coastal|coast|inshore|offshore|ocean|sea|surf|beach|harbor|harbour|marina|bay|reef|kelp|pier|jetty)\b/.test(hay)) return 'saltwater';
  if (/\b(freshwater|fresh water|inland|lake|reservoir|pond|river|creek|stream|brook|canal|waterway)\b/.test(hay)) return 'freshwater';

  if (input.contextClass === 'coastal') return 'saltwater';
  return 'unknown';
}

export function buildFishIQWaterProfile(input: FishIQWaterProfileInput): FishIQWaterProfile {
  const lat = finiteNumberOrNull(input.lat);
  const lng = finiteNumberOrNull(input.lng ?? input.lon);

  const locationContext: FishIQLocationContext = buildFishIQLocationContext({
    lat,
    lon: lng,
    waterName: input.waterName || null,
    waterType: input.waterType || input.waterKind || null,
    waterKind: input.waterKind || input.waterType || null,
    regionHint: input.regionHint || null,
    season: input.season || undefined,
    timeOfDay: input.timeOfDay || undefined,
    source: input.source || null,
  });

  const resolved = resolveWaterContext({
    lat,
    lon: lng,
    waterName: locationContext.waterName,
    waterType: locationContext.waterType,
    waterKind: locationContext.waterKind,
    regionHint: locationContext.regionHint,
  });

  const waterType = normalizeFishIQWaterType({
    waterType: locationContext.waterType,
    waterKind: locationContext.waterKind,
    waterName: locationContext.waterName,
    contextClass: resolved.contextClass,
  });

  return {
    lat: lat ?? 0,
    lng: lng ?? 0,
    waterType,
    waterName: locationContext.waterName || null,
    waterKind: locationContext.waterKind || null,
    regionHint: locationContext.regionHint || null,
    season: locationContext.season || currentSeason(),
    timeOfDay: locationContext.timeOfDay || currentTimeBucket(),
    source: input.source || null,
    contextClass: resolved.contextClass,
    contextReason: resolved.reason,
    generatedAt: Date.now(),
  };
}
